import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlockClipboardContext } from '@/editor/lib/BlockClipboardContext';
import { Button, ToggleButton, ToggleButtonGroup } from '@mui/material';
import Container from '@/components/Container';
import Styles from '@/assets/js/Styles';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import { BlockType } from '../../declarations/models/BlockType';
import { BlockIcon } from '../../components/BlockIcon';
import ButtonCollection, { addButtonSx } from '../../components/ButtonCollection';
import { BaseBlock } from '../../declarations/models/blocks/BaseBlock';
import { BlockModelFactory } from '../lib/factories/BlockModelFactory';
import { useEditorConfiguration } from '../lib/configuration/EditorConfigurationContext';
import { useCurrentSection } from './CurrentSectionProvider';
import ModuleClipboardMenu from './ModuleClipboardMenu';
import { canChooseAddPosition, useAddBlockPosition } from './useAddBlockPosition';

export interface AddBlockToPageSectionButtonGroupProps {
  onAddBlock: (block: BaseBlock) => void;
  onPaste?: () => void;
  /** Pastes a module from the clipboard that is kept across pages */
  onPasteFromClipboard?: (block: BaseBlock) => void;
}

export const AddBlockButton: FC<AddBlockToPageSectionButtonGroupProps> = ({
  onAddBlock,
  onPaste,
  onPasteFromClipboard,
}) => {
  const { t } = useTranslation('common');
  const { sectionType, section } = useCurrentSection();
  const config = useEditorConfiguration();

  const hiddenBlocks = config.getHiddenBlocks(sectionType);

  const { currentClipboard } = useBlockClipboardContext();
  const [position, setPosition] = useAddBlockPosition();

  const handleAddBlock = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, blockType: BlockType) =>
    onAddBlock(BlockModelFactory.makeBlockModel(blockType));
  const getLabel = (blockType: BlockType) => t(`BlockType.${blockType}`);
  const getIcon = (blockType: BlockType) => <BlockIcon blockType={blockType} />;
  const getButtonColor = (blockType: BlockType) => {
    if (
      [
        BlockType.DM_AUTO,
        BlockType.DM_FILTER_SEARCH,
        BlockType.DM_SINGLE,
        BlockType.DM_SINGLE,
        BlockType.DM_MANUAL,
        BlockType.DM_SCHOOL,
        BlockType.KP,
        BlockType.KP_OWNER,
      ].includes(blockType)
    ) {
      return Styles.Colors.LIGHTEST_BLUE;
    }

    if ([BlockType.EVENTS_AUTO, BlockType.EVENTS_MANUAL].includes(blockType)) {
      return Styles.Colors.STRONG_GREEN_TRANSPARENT;
    }
  };

  const canPaste = currentClipboard && config.isBlockAvailableInSection(sectionType, currentClipboard.value.type);

  return (
    <>
      {section?.introSection && (
        <Container
          column
          fullWidth
          p={2}
          mt={4}
          mb={0}
          gap={0}
          left
          sx={{
            backgroundColor: Styles.Colors.LIGHT_BLUE,
            borderRadius: Styles.Dimensions.SECTION_BORDER_RADIUS,
            border: `1px solid ${Styles.Colors.MEDIUM_BLUE}`,
            fontStyle: 'italic',
            fontSize: '15px',
          }}>
          {t('IntroSection.limitations')}
        </Container>
      )}
      <ButtonCollection
        title={t('add')}
        titleAdornment={
          canChooseAddPosition(sectionType) && (
            <ToggleButtonGroup
              size='small'
              exclusive
              value={position}
              onChange={(_, value) => value && setPosition(value)}
              aria-label={t('addPosition')}
              sx={{
                ml: 1.5,
                verticalAlign: 'middle',
                backgroundColor: Styles.Colors.WHITE,
                '& .MuiToggleButton-root': {
                  py: 0.25,
                  px: 1,
                  gap: 0.5,
                  fontSize: '0.75rem',
                  lineHeight: 1.5,
                  textTransform: 'none',
                },
                '& .MuiToggleButton-root.Mui-selected': {
                  backgroundColor: Styles.Colors.LIGHT_GREY,
                  color: 'text.primary',
                  fontWeight: 600,
                },
              }}>
              <ToggleButton value='top' title={t('addAtTop')}>
                <MaterialSymbol name='vertical_align_top' sx={{ fontSize: 18 }} />
                {t('addAtTopShort')}
              </ToggleButton>
              <ToggleButton value='bottom' title={t('addAtBottom')}>
                <MaterialSymbol name='vertical_align_bottom' sx={{ fontSize: 18 }} />
                {t('addAtBottomShort')}
              </ToggleButton>
            </ToggleButtonGroup>
          )
        }
        buttons={[...config.getBlocksAvailableInSection(sectionType)]}
        onClick={handleAddBlock}
        getButtonKey={String}
        getButtonLabel={getLabel}
        getButtonIcon={getIcon}
        getButtonColor={getButtonColor}
        getIsHideable={(blockType) => hiddenBlocks.includes(blockType)}>
        {canPaste && (
          <Button
            color='secondary'
            onClick={onPaste}
            sx={{
              ...addButtonSx,
              backgroundColor: Styles.Colors.COPY_PASTE_PURPLE_LIGHT,
              '&:hover': {
                border: `1px solid`,
              },
            }}>
            <MaterialSymbol name='content_paste' />
            {t('pasteName', { name: t(`BlockType.${currentClipboard?.value.type}`) })}
          </Button>
        )}
        {onPasteFromClipboard && (
          <ModuleClipboardMenu
            canPaste={(block) => config.isBlockAvailableInSection(sectionType, block.type)}
            onPaste={onPasteFromClipboard}
          />
        )}
      </ButtonCollection>
    </>
  );
};

export default AddBlockButton;
